// Camera pose of the local player: predicted position interpolated between the last two predicted
// ticks, plus the visual error offset, eye height, landing dip, live view angles, roll and FOV.

import { TICK_DT } from "../net/constants";
import type { PlayerView, Vec3Like } from "../wasm/types";
import { wrapAngle } from "./angles";
import {
  FOV_PULSE_MAX_DEG,
  FOV_PULSE_TAU_SEC,
  LandingDip,
  ROLL_TAU_SEC,
  approachExp,
  dynamicFovBonus,
  rollTarget,
  zoomedHFov,
} from "./cameraMath";
import { VisualError } from "./visualError";

export interface CameraPose {
  pos: Vec3Like;
  yaw: number;
  pitch: number;
  roll: number;
  hFovDeg: number;
}

export interface CameraInputs {
  /** Live view angles (radians) including recoil. */
  yaw: number;
  pitch: number;
  baseFovDeg: number;
  dynamicFov: boolean;
  runSpeed: number;
  hopSpeedCap: number;
}

/** Time constant of the dynamic FOV smoothing. */
const FOV_TAU_SEC = 0.22;
const STAND_EYE = 1.62;
/**
 * A walking player's feet move vertically by more than this (m) in one tick beyond what the
 * velocity explains: a step up or a snap down the sim does in a single tick (up to 0.45 m). The
 * camera eases through it instead of popping.
 */
const STEP_SMOOTH_MIN = 0.06;
/** Seconds the camera sinks after a death before the death cam follows the killer. */
const DEATH_SINK_SEC = 0.5;

export class CameraRig {
  readonly error = new VisualError();
  readonly dip = new LandingDip();
  readonly pose: CameraPose = {
    pos: { x: 0, y: 0, z: 0 },
    yaw: 0,
    pitch: 0,
    roll: 0,
    hFovDeg: 103,
  };

  private readonly prev = { x: 0, y: 0, z: 0 };
  private readonly cur = { x: 0, y: 0, z: 0 };
  private prevEye = STAND_EYE;
  private curEye = STAND_EYE;
  private grounded = false;
  private roll = 0;
  private fovBonus = 0;
  /** Short FOV widening that marks a movement event (see `pulseFov`). */
  private fovPulse = 0;
  private lastYaw = 0;
  private haveYaw = false;
  private deathT = -1;
  private deathBaseY = 0;

  /** Snap everything to `view` (spawn, resync). */
  reset(view: PlayerView): void {
    this.set(this.prev, view.pos);
    this.set(this.cur, view.pos);
    this.prevEye = this.curEye = view.eyeHeight;
    this.grounded = view.onGround;
    this.error.clear();
    this.dip.reset();
    this.roll = 0;
    this.fovBonus = 0;
    this.fovPulse = 0;
    this.haveYaw = false;
    this.deathT = -1;
  }

  /** Widens the FOV by `deg` degrees (capped) and lets it settle back: perfect hops, slide resets, wall jumps. */
  pulseFov(deg: number): void {
    this.fovPulse = Math.min(this.fovPulse + deg, FOV_PULSE_MAX_DEG);
  }

  /** A predicted tick finished: `view` is its result. */
  push(view: PlayerView): void {
    this.set(this.prev, this.cur);
    this.set(this.cur, view.pos);
    this.prevEye = this.curEye;
    this.curEye = view.eyeHeight;

    // Stairs and ledges: on the ground the feet move vertically only as the velocity says, except
    // for a step up / snap down, which the sim does within one tick. Keep the camera at its old
    // height (the previous position moves with the step, the error offset holds the difference)
    // and let the offset decay.
    const onGround = view.onGround && !view.mantling;
    const stepped = this.cur.y - this.prev.y - view.vel.y * TICK_DT;
    if (this.grounded && onGround && Math.abs(stepped) > STEP_SMOOTH_MIN) {
      this.prev.y += stepped;
      this.error.add(0, -stepped, 0);
    }
    this.grounded = onGround;
  }

  /**
   * Reconciliation moved the latest predicted state by `new - old = -(errX, errY, errZ)`. The
   * previous tick moves with it so the interpolated position changes by the same amount, and the
   * error offset hides the jump. Returns false when the camera snapped (> 2 m).
   */
  correct(errX: number, errY: number, errZ: number, view: PlayerView): boolean {
    this.grounded = view.onGround && !view.mantling;
    this.prev.x -= errX;
    this.prev.y -= errY;
    this.prev.z -= errZ;
    this.set(this.cur, view.pos);
    this.curEye = view.eyeHeight;
    return this.error.add(errX, errY, errZ);
  }

  /** Frame update while alive. `alpha` is the position inside the current tick (0..1). */
  update(
    dt: number,
    alpha: number,
    view: PlayerView,
    inp: CameraInputs,
  ): CameraPose {
    this.error.decay(dt);
    const dip = this.dip.step(dt);

    const a = alpha < 0 ? 0 : alpha > 1 ? 1 : alpha;
    const eye = this.prevEye + (this.curEye - this.prevEye) * a;
    const p = this.pose;
    p.pos.x = this.prev.x + (this.cur.x - this.prev.x) * a + this.error.x;
    p.pos.y =
      this.prev.y + (this.cur.y - this.prev.y) * a + eye + this.error.y + dip;
    p.pos.z = this.prev.z + (this.cur.z - this.prev.z) * a + this.error.z;
    p.yaw = inp.yaw;
    p.pitch = inp.pitch;

    // Roll toward the pose of the movement state.
    const yawRate =
      this.haveYaw && dt > 0 ? wrapAngle(inp.yaw - this.lastYaw) / dt : 0;
    this.lastYaw = inp.yaw;
    this.haveYaw = true;
    const lateral =
      view.vel.x * Math.cos(inp.yaw) - view.vel.z * Math.sin(inp.yaw);
    const target = rollTarget({
      wallrunSide: view.wallrunSide,
      sliding: view.sliding,
      lateralSpeed: lateral,
      yawRate,
      runSpeed: inp.runSpeed,
    });
    this.roll = approachExp(this.roll, target, ROLL_TAU_SEC, dt);
    p.roll = this.roll;

    const bonusTarget = inp.dynamicFov
      ? dynamicFovBonus(view.speed, inp.runSpeed, inp.hopSpeedCap)
      : 0;
    this.fovBonus = approachExp(this.fovBonus, bonusTarget, FOV_TAU_SEC, dt);
    this.fovPulse = approachExp(this.fovPulse, 0, FOV_PULSE_TAU_SEC, dt);
    // The pulse is part of the dynamic FOV: players who turned that off get a steady view.
    const pulse = inp.dynamicFov ? this.fovPulse : 0;
    p.hFovDeg = zoomedHFov(
      inp.baseFovDeg + this.fovBonus + pulse,
      view.adsFrac,
      view.adsFovMult,
    );
    return p;
  }

  /** The player died: the pose is held and sinks with a roll over half a second. */
  startDeath(): void {
    this.deathT = 0;
    this.deathBaseY = this.pose.pos.y;
  }

  /** The death sink has played out (or no death is playing): a spectator camera may take over. */
  deathSettled(): boolean {
    return this.deathT < 0 || this.deathT >= DEATH_SINK_SEC;
  }

  /** Frame update while dead and not spectating. */
  updateDead(dt: number): CameraPose {
    const p = this.pose;
    if (this.deathT >= 0) {
      this.deathT = Math.min(DEATH_SINK_SEC, this.deathT + dt);
      const k = this.deathT / DEATH_SINK_SEC;
      const ease = k * k * (3 - 2 * k);
      p.pos.y = this.deathBaseY - 0.9 * ease;
      p.roll = ease * 0.35;
    }
    return p;
  }

  private set(to: { x: number; y: number; z: number }, from: Vec3Like): void {
    to.x = from.x;
    to.y = from.y;
    to.z = from.z;
  }
}
